import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DomHandler } from 'primeng/dom';
import { OverlayPanel } from 'primeng/overlaypanel';
import { map, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AccelaControl, AccelaControlType } from '../../model/accelaControl.model';
import { ApiProviderService } from 'src/app/services/api-provider.service';
import { AdminSelectionEvent } from '../../model/adminSelectionEvent';
import { Editor } from 'primeng/editor';
import { PageLayoutService } from '../../services/page-layout.service';
import { SettingService } from 'src/app/modules/shared/services/setting.service';

@Component({
  selector: 'aca-admin-page-layout',
  templateUrl: './admin-page-layout.component.html',
  styleUrls: ['./admin-page-layout.component.less']
})
export class AdminPageLayoutComponent implements AfterViewInit, OnDestroy {

  @Input() viewId: string;
  @Input() module: string;
  @Input() permissionLevel: string;
  @Input() permissionValue: string;
  @Input() loadLayoutOnInit = true;
  @Input() multiCol = false;

  @ViewChild('op') adminUpdatePanel: OverlayPanel;
  @ViewChild('FieldLabelContent') fieldLabelContent: ElementRef;
  @ViewChild('FieldTooltip') fieldTooltip: Editor;
  @ViewChild('FieldWatermark') fieldWatermark: ElementRef;
  tooltipValue = '';
  controlGroup = this.formBuilder.group({});
  simpleViewElementList: AccelaControl[];
  displayFormDesigner = false;
  showDrpDownOptions = false;
  displayFieldSelection = false;
  adminSelectedControl: AccelaControl;
  controlTypes = AccelaControlType;
  interactionObserver: IntersectionObserver;
  formDesignerLink: SafeResourceUrl = this.sanitizer
    .bypassSecurityTrustResourceUrl(this.apiRefService
      .getApiUrl('Admin/NGACAFormDesigner.aspx'));
  hideFields: Array<string> = [
    'txtAppStreetAdd1',
    'txtAppStreetAdd2',
    'txtAppStreetAdd3',
    'txtAppCity',
    'ddlAppCountry',
    'txtAppFax',
    'txtAppState',
    'txtAppZipApplicant'
  ];
  isContactAddressEnabled = false;
  protected destoryActionSbj = new Subject<boolean>();

  constructor(private pageLayoutService: PageLayoutService, private sanitizer: DomSanitizer, private apiRefService: ApiProviderService,
    private formBuilder: UntypedFormBuilder, private settingService: SettingService) { }

  ngAfterViewInit(): void {
    this.formDesignerLink = this.sanitizer
      .bypassSecurityTrustResourceUrl(this.apiRefService
        .getApiUrl(`Admin/NGACAFormDesigner.aspx?module=${this.module}&viewId=${this.viewId}` +
          `&permissionLevel=${this.permissionLevel}&permissionValue=${this.permissionValue}&sectionName=`));

    //uses the IntersectionObserver to track if an element is in danger of being pushed off the screen
    this.interactionObserver = new IntersectionObserver(this.onIntersection, {
      root: null,   // default is the viewport
      threshold: .65 // percentage of taregt's visible area. Triggers "onIntersection"
    });
    if (this.loadLayoutOnInit) {
      this.refreshPageLayout();
    }
    if (this.adminUpdatePanel && this.adminUpdatePanel.el) {
      this.interactionObserver.observe(this.adminUpdatePanel.el.nativeElement);
    }
  }

  // callback is called on intersection change
  onIntersection(entries: Array<IntersectionObserverEntry>, opts: IntersectionObserver) {
    entries.forEach(entry =>
      entry.target.classList.toggle('admin-panel-flip', entry.isIntersecting)
    );
  }
  //Unsort the elements by keyvalue in FormGroup
  unsorted() {
    return 0;
  }

  adminSelection(ev: AdminSelectionEvent) {
    this.adminSelectedControl = ev.controlData;
    this.tooltipValue = this.adminSelectedControl.tooltip;
    const hostedDiv = ev.elementRef.closest('.accelaControl');
    this.showDrpDownOptions = (this.adminSelectedControl.type === AccelaControlType.Dropdown
      && this.adminSelectedControl.name !== 'ddlQuestion'   //Security Questions
      && !this.adminSelectedControl.templateField);   //template field  
    hostedDiv.insertAdjacentElement('afterend', this.adminUpdatePanel.el.nativeElement);
    if (!this.adminUpdatePanel.container) {
      this.adminUpdatePanel.container = this.adminUpdatePanel.el.nativeElement.firstElementChild;
    }
    this.adminUpdatePanel.show(ev.elementRef, hostedDiv);

    if (this.adminUpdatePanel.el.nativeElement.classList.contains('admin-panel-flip')) {
      DomHandler.addClass(this.adminUpdatePanel.container, 'p-overlaypanel-flipped');
      if (this.adminUpdatePanel.container) {
        const elementDimensions = this.adminUpdatePanel.container.offsetParent ?
          { width: this.adminUpdatePanel.container.offsetWidth, height: this.adminUpdatePanel.container.offsetHeight } :
          DomHandler.getHiddenElementDimensions(this.adminUpdatePanel.container);

        let top = hostedDiv.getBoundingClientRect().top + DomHandler.getWindowScrollTop() - elementDimensions.height;
        this.adminUpdatePanel.container.style.transformOrigin = 'bottom';

        if (top < 0) {
          top = DomHandler.getWindowScrollTop();
        }
        this.adminUpdatePanel.container.style.top = top + 'px';
      }

    }

  }

  submitAdminContentUpdate(controlData: AccelaControl) {

    if (this.fieldTooltip.getQuill().getText().length > 1) {
      controlData.tooltip = this.fieldTooltip.getQuill().root.innerHTML?.trim();
    } else {
      controlData.tooltip = '';
    }
    controlData.placeholder = this.fieldWatermark.nativeElement.value;
    controlData.label = this.fieldLabelContent.nativeElement.value;

    if (this.showDrpDownOptions) {
      //Removing the blank value options
      for (let i = 0; i < this.adminSelectedControl.options.length; i++) {
        if (this.adminSelectedControl.options[i].value === '') {
          this.adminSelectedControl.options.splice(i--, 1);
        }
      }
      controlData.options = this.adminSelectedControl.options;
    }

    this.pageLayoutService.saveUpdatedControl(controlData, this.module, this.viewId).pipe(
      map(res => {
        this.adminUpdatePanel.hide();
      }),
      takeUntil(this.destoryActionSbj)
    ).subscribe(() => this.refreshPageLayout());
  }

  onValChangeEvent(event: any, index: number) {
    this.adminSelectedControl.options[index].label = event.target.value;
    this.adminSelectedControl.options[index].value = event.target.value;
  }

  addField() {
    this.adminSelectedControl.options.push({ label: '', value: '' });
  }

  deleteField(index) {
    this.adminSelectedControl.options.splice(index, 1);
  }


  submitSelectedFieldsUpdate() {
    this.pageLayoutService
      .saveUpdatedControlProperties(this.simpleViewElementList, this.module, this.viewId, this.permissionLevel, this.permissionValue).pipe(
        map(res => {
          this.displayFieldSelection = false;
        }),
        takeUntil(this.destoryActionSbj)
      ).subscribe(() => this.refreshPageLayout());
  }

  launchRearrangeFieldWorkflow() {
    this.formDesignerLink = this.sanitizer
      .bypassSecurityTrustResourceUrl(this.apiRefService
        .getApiUrl(`Admin/NGACAFormDesigner.aspx?module=${this.module}&viewId=${this.viewId}` +
          `&permissionLevel=${this.permissionLevel}&permissionValue=${this.permissionValue}&sectionName=`));
    this.displayFormDesigner = true;
  }

  launchSelectFieldsWorkflow() {
    this.displayFieldSelection = true;
  }

  refreshPageLayout() {
    this.controlGroup = this.formBuilder.group({});
    this.simpleViewElementList = [];
    this.settingService.isContactAddressEnabled()
      .subscribe((res) => {
        if (res) {
          this.isContactAddressEnabled = true;
        }
        this.loadAdminPageLayout();
      });

  }

  loadAdminPageLayout() {
    this.pageLayoutService
      .getDynamicFormData(this.viewId, this.module, this.permissionLevel, this.permissionValue, 'ACA ADMIN').pipe(map(controls => {
        this.simpleViewElementList = controls
          .filter(controlData =>
            controlData.type !== AccelaControlType.Separator
            && controlData.type !== AccelaControlType.Blocked
            && !controlData.templateField);

        if (this.isContactAddressEnabled) {
          this.simpleViewElementList = this.simpleViewElementList.filter(cntrl => !this.hideFields.includes(cntrl.name));
        }
        controls.forEach(controlData => {
          if (this.isContactAddressEnabled) {
            if (this.hideFields.indexOf(controlData.name) > -1) {
              controlData.display = false;
              controlData.required = false;
            }
          }

          if (this.multiCol) {
            if (controlData.display && controlData.type !== AccelaControlType.Separator) {
              const abstractControl = new UntypedFormControl(controlData.value);
              controlData.readOnly = true;

              this.pageLayoutService.processAccelaControl(controlData, abstractControl);
              this.controlGroup.addControl(controlData.name, abstractControl);
            }
          } else {
            if (controlData.display) {
              const abstractControl = new UntypedFormControl(controlData.value);
              controlData.readOnly = true;

              this.pageLayoutService.processAccelaControl(controlData, abstractControl);
              this.controlGroup.addControl(controlData.name, abstractControl);
            }
          }

        });
        this.adminSelectedControl = controls[0];
      }),
        takeUntil(this.destoryActionSbj))
      .subscribe();
  }

  selectedColChange(rowData: AccelaControl) {    
    if (rowData.display === false) {
      rowData.required = false;      
    }
  }

  ngOnDestroy() {
    this.destoryActionSbj.next();
    this.destoryActionSbj.complete();

    if (this.interactionObserver)
      this.interactionObserver.disconnect();
  }

}
